import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ResponsiveContainer, AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, LabelList } from 'recharts';
import { Pencil, ArrowLeftRight, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0)).replace(/\u202f|,/g, ' ');
const card = 'bg-white rounded-2xl border border-slate-200 shadow-sm';
export const TYPE_COLOR = { 'Panneau déroulant': '#2563eb', 'Panneau LED': '#7c3aed', 'Grand format': '#0d9488', 'Panneau poteau': '#ea7a1a' };
const DESC = {
  'Panneau déroulant': 'Faces en rotation, plusieurs annonceurs', 'Panneau LED': 'Écrans lumineux, diffusion en boucle',
  'Grand format': 'Grandes faces fixes très visibles', 'Panneau poteau': 'Petits formats le long des axes',
};
const TEMPLATES = [
  { nom: 'Conception graphique', description: 'Création du visuel de la campagne', prix: 35000, unite: 'par visuel', Icon: Pencil },
  { nom: 'Transport', description: 'Livraison du matériel et déplacement des équipes', prix: 20000, unite: 'par trajet', Icon: ArrowLeftRight },
];

export function TypeTarifCards({ types, countByType, onCreate, onList }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="tarifs-type-picker">
      {types.map((t) => {
        const n = countByType(t.key);
        return (
          <div key={t.key} className={`${card} p-4 flex items-center gap-4`} data-testid={`tarif-card-${t.key}`}>
            <div className={`w-12 h-12 rounded-xl ${t.iconBg} flex items-center justify-center text-white text-xl font-black flex-shrink-0`}>{t.letter}</div>
            <div className="min-w-0 flex-1">
              <div className="font-bold text-slate-900">{t.title}</div>
              <div className="text-sm text-slate-600 leading-snug">{t.description}</div>
              <div className="text-xs text-slate-400 mt-0.5">{t.bullets.join(' · ')}</div>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <button onClick={() => n && onList(t.key)} className="text-slate-500 text-sm" data-testid={`btn-view-grids-${t.key}`}>
                <b className="text-2xl text-slate-900">{n}</b> grille{n > 1 ? 's' : ''}
              </button>
              <Button onClick={() => onCreate(t.key)} className={`${t.btn} text-white h-9 px-3 font-bold gap-1`} data-testid={`btn-create-${t.key}`}>
                <Plus className="h-4 w-4" /> Créer une grille
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PanelPriceCards({ overview, onView, onEdit }) {
  return (
    <div className={`${card} p-5`} data-testid="tarifs-par-type">
      <h3 className="text-lg font-bold text-slate-900">Tarifs par type de panneau</h3>
      <p className="text-sm text-slate-500">Prix mensuel actuel et évolution sur 6 mois</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mt-4">
        {(overview?.types || []).map((t) => {
          const c = TYPE_COLOR[t.type_panneau];
          const data = (overview.months || []).map((m, i) => ({ m, v: t.series[i] }));
          return (
            <div key={t.type_panneau} className="rounded-xl border border-slate-200 p-4" data-testid={`price-card-${t.court}`}>
              <div className="flex gap-3">
                <span className="w-1.5 rounded-full self-stretch" style={{ background: c }} />
                <div><div className="font-bold text-slate-900">{t.titre}</div><div className="text-sm text-slate-500">{DESC[t.type_panneau]}</div></div>
              </div>
              <div className="text-right text-sm font-bold mt-2 h-5" style={{ color: t.trend_pct > 0 ? '#15803d' : t.trend_pct < 0 ? '#dc2626' : '#94a3b8' }} data-testid={`price-trend-${t.court}`}>
                {t.prix_mois ? (t.trend_pct ? `${t.trend_pct > 0 ? '▲ +' : '▼ '}${String(t.trend_pct).replace('.', ',')} %` : '= stable') : ''}
              </div>
              <div className="flex items-end justify-between gap-2">
                {t.prix_mois
                  ? <div><span className="text-3xl font-extrabold text-slate-900" data-testid={`price-value-${t.court}`}>{fmt(t.prix_mois)}</span> <span className="text-sm text-slate-500">FCFA / mois</span></div>
                  : <div className="text-sm text-slate-400 py-2">Aucun tarif défini</div>}
                <div className="w-28 h-10">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data}><Area type="monotone" dataKey="v" stroke={c} strokeWidth={2} fill={c} fillOpacity={0.12} connectNulls dot={false} /></AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 border-t border-slate-100 mt-3 pt-3 text-xs text-slate-500">
                <div>Durée minimale<div className="text-sm font-bold text-slate-900">{t.duree_min}</div></div>
                <div>Frais de pose<div className="text-sm font-bold text-slate-900">{t.frais_pose ? `${fmt(t.frais_pose)} FCFA` : '—'}</div></div>
                <div>Grilles<div className="text-sm font-bold text-slate-900">{t.grilles}</div></div>
              </div>
              <div className="flex items-center justify-between mt-3">
                {t.last_grid_id
                  ? <button onClick={() => onView(t)} className="text-sm font-bold hover:underline" style={{ color: c }} data-testid={`btn-voir-${t.court}`}>Voir le tarif</button>
                  : <span className="text-sm text-slate-400">Pas encore de grille</span>}
                <Button variant="outline" size="sm" onClick={() => onEdit(t)} className="h-8 font-bold" data-testid={`btn-modifier-${t.court}`}>{t.last_grid_id ? 'Modifier' : 'Créer'}</Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function EvolutionPrix({ overview }) {
  const types = overview?.types || [];
  const data = (overview?.months || []).map((m, i) => {
    const row = { m };
    types.forEach((t) => { row[t.court] = t.series[i] ? Math.round(t.series[i] / 1000) : null; });
    return row;
  });
  return (
    <div className={`${card} p-5`} data-testid="tarifs-evolution">
      <h3 className="text-lg font-bold text-slate-900">Évolution des prix moyens</h3>
      <p className="text-sm text-slate-500">En milliers de FCFA par mois, de {overview?.months?.[0]?.toLowerCase()} à {overview?.months?.[5]?.toLowerCase()}</p>
      <div className="flex gap-4 mt-3 text-sm text-slate-700 flex-wrap">
        {types.map((t) => <span key={t.court} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm" style={{ background: TYPE_COLOR[t.type_panneau] }} />{t.court}</span>)}
      </div>
      <div className="h-72 mt-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 10, right: 40, left: -10, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="#e2e8f0" />
            <XAxis dataKey="m" tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <YAxis tickLine={false} axisLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
            <Tooltip formatter={(v) => `${v} k FCFA`} />
            {types.map((t) => (
              <Line key={t.court} type="monotone" dataKey={t.court} stroke={TYPE_COLOR[t.type_panneau]} strokeWidth={2.5} connectNulls dot={{ r: 4, fill: '#fff', strokeWidth: 2 }}>
                <LabelList dataKey={t.court} position="right" content={({ x, y, value, index }) => (index === data.length - 1 && value ? <text x={x + 8} y={y + 4} fill={TYPE_COLOR[t.type_panneau]} fontSize={12} fontWeight={700}>{value}</text> : null)} />
              </Line>
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ServiceDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ nom: '', description: '', prix: '', unite: 'par prestation', ...initial });
  const save = async () => {
    if (!f.nom.trim()) return toast.error('Nom requis');
    const body = { ...f, prix: parseFloat(f.prix) || 0 };
    try {
      if (f.id) await axios.put(`${API}/api/tarifs/services/${f.id}`, body);
      else await axios.post(`${API}/api/tarifs/services`, body);
      toast.success('Service enregistré');
      onSaved();
    } catch { toast.error("Impossible d'enregistrer"); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="service-dialog">
        <DialogHeader><DialogTitle>{f.id ? 'Modifier le service' : 'Créer le tarif du service'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nom</Label><Input value={f.nom} onChange={(e) => setF({ ...f, nom: e.target.value })} data-testid="service-nom" /></div>
          <div><Label>Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="service-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Prix (FCFA)</Label><Input type="number" value={f.prix} onChange={(e) => setF({ ...f, prix: e.target.value })} data-testid="service-prix" /></div>
            <div><Label>Unité</Label><Input value={f.unite} onChange={(e) => setF({ ...f, unite: e.target.value })} data-testid="service-unite" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Annuler</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="service-save">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ServicesCard({ overview, onChanged }) {
  const [services, setServices] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get(`${API}/api/tarifs/services`).then((r) => setServices(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Supprimer « ${s.nom} » ?`)) return;
    await axios.delete(`${API}/api/tarifs/services/${s.id}`);
    load(); onChanged();
  };
  const pending = TEMPLATES.filter((t) => !services.some((s) => s.nom.toLowerCase() === t.nom.toLowerCase()));
  const types = overview?.types || [];
  const maxG = Math.max(1, ...types.map((t) => t.grilles));
  const Row = ({ s, tpl }) => {
    const Icon = s.Icon || Pencil;
    return (
      <div className="rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`service-${tpl ? 'tpl' : 'row'}-${s.nom}`}>
        <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0"><Icon className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1"><div className="font-bold text-slate-900">{s.nom}</div><div className="text-sm text-slate-500 truncate">{s.description}</div></div>
        <div className="text-right"><div className="font-extrabold text-slate-900">{fmt(s.prix)} FCFA</div><div className="text-xs text-slate-500">{s.unite}</div></div>
        <div className="flex flex-col items-end gap-1">
          {tpl && <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-50 text-amber-700">À créer</span>}
          {tpl
            ? <Button size="sm" onClick={() => setEditing({ nom: s.nom, description: s.description, prix: s.prix, unite: s.unite })} className="h-8 bg-blue-600 hover:bg-blue-700 font-bold" data-testid={`btn-create-service-${s.nom}`}>Créer le tarif</Button>
            : <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setEditing(s)} className="h-8" data-testid={`btn-edit-service-${s.id}`}>Modifier</Button>
                <Button size="sm" variant="ghost" onClick={() => remove(s)} className="h-8 text-red-500" data-testid={`btn-del-service-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>}
        </div>
      </div>
    );
  };
  return (
    <div className={`${card} p-5`} data-testid="tarifs-services">
      <h3 className="text-lg font-bold text-slate-900">Services à tarifer</h3>
      <p className="text-sm text-slate-500">Frais en plus de la location des panneaux</p>
      <div className="space-y-3 mt-4">
        {services.map((s) => <Row key={s.id} s={s} />)}
        {pending.map((t) => <Row key={t.nom} s={t} tpl />)}
        <button onClick={() => setEditing({})} className="w-full rounded-xl border-2 border-dashed border-slate-200 py-3 text-blue-700 font-bold hover:bg-blue-50 transition-colors" data-testid="btn-add-service">
          + Ajouter un autre service <span className="font-normal text-sm text-slate-500">(pose, impression…)</span>
        </button>
      </div>
      <div className="border-t border-slate-100 mt-5 pt-4">
        <h4 className="font-bold text-slate-900">Grilles créées par type de panneau</h4>
        <div className="space-y-2 mt-3">
          {types.map((t) => (
            <div key={t.court} className="grid grid-cols-[110px_1fr_24px] items-center gap-3 text-sm" data-testid={`grilles-bar-${t.court}`}>
              <span className="text-slate-700">{t.court}</span>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(t.grilles / maxG) * 100}%`, background: TYPE_COLOR[t.type_panneau] }} /></div>
              <b className="text-right text-slate-900">{t.grilles}</b>
            </div>
          ))}
        </div>
      </div>
      {editing && <ServiceDialog initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChanged(); }} />}
    </div>
  );
}
